import { memo, useCallback } from "react";
import { generatePath, Link, useNavigate } from "react-router";

import type { BillingStatus, TicketListAvailable_BillingPageQuery } from "@/lib/gql/__generated__/graphql";

import { externalLink } from "@/routes/_constants/external-link";
import { PATHS } from "@/routes/paths";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog/dialog";

import { BasicTicketPurchaseTicketSection } from "./components/island/basic-ticket/section/purchase-ticket";
import { ProTicketCurrentTicketSection } from "./components/island/pro-ticket/components/current-ticket";
import { ProTicketRecurringPurchaseTicketSection } from "./components/island/pro-ticket/components/recurring-purchase-ticket";

import type {
	BillingSettingDialogStateType,
	BillingSettingDialogType,
	OnUpdatePurchaseBasicBillingTicketType,
	OnUpdatePurchaseProBillingTicketType,
	OnUpdateRecurringPurchaseProBillingTicketType,
} from "../../../model/use-case";

type Props = {
	hotelID: string;

	ticketBasicListAvailable: TicketListAvailable_BillingPageQuery["ticketBasicListAvailable"];
	ticketProListAvailableContinuousBilling: TicketListAvailable_BillingPageQuery["ticketProListAvailableContinuousBilling"];
	ticketProListAvailableSingleBilling: TicketListAvailable_BillingPageQuery["ticketProListAvailableSingleBilling"];

	isUsingPro: boolean;

	billingStatus: BillingStatus;
	onPurchaseBasicBillingTicket: OnUpdatePurchaseBasicBillingTicketType;
	onPurchaseProBillingTicket: OnUpdatePurchaseProBillingTicketType;
	onRecurringPurchaseProBillingTicket: OnUpdateRecurringPurchaseProBillingTicketType;

	openBillingSettingDialog: BillingSettingDialogStateType;
	handleOpenChange: (type: BillingSettingDialogType) => (callBackFunc: NodeJS.Timeout) => (open: boolean) => void;
	activateBillingSettingDialog: (type: BillingSettingDialogType) => void;
	deactivateBillingSettingDialog: (type: BillingSettingDialogType) => void;
};

/**
 * CHILLNN サポート「CHILLNNの費用について（基本料金/手数料タイプ）」
 * https://support.chillnn.com/faq/host/settlement/862fffe15c57825292c28192adfaa971
 *
 * ベースURLは externalLink.faqSite（dev/prd で自動切替）を使う。
 * パスは Notion ページ ID で構成されるため、日本語タイトルや旧スラッグは使わない。
 */
const URL = `${externalLink.faqSite}/faq/host/settlement/862fffe15c57825292c28192adfaa971`;

const SectionWrapper = ({ children, title }: { children: React.ReactNode; title: string }) => {
	return (
		<div className="space-y-2">
			<h5 className="text-lg font-semibold">{title}</h5>

			<div className="ml-0 tablet:ml-4">{children}</div>
		</div>
	);
};

export const SettingsBillingDialog = memo(
	({
		hotelID,
		ticketBasicListAvailable,
		ticketProListAvailableContinuousBilling,
		ticketProListAvailableSingleBilling,

		billingStatus,
		onPurchaseBasicBillingTicket: _onPurchaseBasicBillingTicket,
		onPurchaseProBillingTicket,
		onRecurringPurchaseProBillingTicket,

		openBillingSettingDialog,
		handleOpenChange,
		activateBillingSettingDialog,
		deactivateBillingSettingDialog,
	}: Props) => {
		const navigate = useNavigate();

		/**
		 * 現在のページをリロードする
		 */
		const reloadCurrentPage = useCallback(() => {
			navigate(0);
		}, [navigate]);

		const onPurchaseBasicBillingTicket: OnUpdatePurchaseBasicBillingTicketType = useCallback(
			async ({ defaultBasicBillingTicketID }) => {
				const result = await _onPurchaseBasicBillingTicket({ defaultBasicBillingTicketID });

				if (result) {
					reloadCurrentPage();
				}

				return result;
			},
			[_onPurchaseBasicBillingTicket, reloadCurrentPage],
		);

		const navigateToHotelTopPage = useCallback(() => {
			navigate(generatePath(PATHS.HOTEL.TOP, { hotelID }));
		}, [hotelID, navigate]);

		return (
			<Dialog open>
				<DialogContent hideCloseButton className="max-w-4xl">
					<DialogHeader className="text-xl font-semibold">料金プランの登録</DialogHeader>

					<div className="space-y-10">
						<div className="space-y-1">
							<div>現在の料金プランでは、この機能は使えません。</div>
							<div className="flex flex-wrap gap-x-2">
								<p>基本料金や予約手数料に関する詳しい内容は</p>
								<Link
									to={URL}
									target="_blank"
									className="text-link-400 underline-offset-4 hover:underline"
									viewTransition
								>
									こちら
								</Link>
							</div>
						</div>

						<div className="space-y-4">
							<SectionWrapper title="基本料金">
								<BasicTicketPurchaseTicketSection
									hotelID={hotelID}
									billingStatus={billingStatus}
									onPurchaseBasicBillingTicket={onPurchaseBasicBillingTicket}
									ticketBasicListAvailable={ticketBasicListAvailable}
									activateBillingSettingDialog={activateBillingSettingDialog}
									deactivateBillingSettingDialog={deactivateBillingSettingDialog}
									handleOpenChange={handleOpenChange}
									openBillingSettingDialog={openBillingSettingDialog}
								/>
							</SectionWrapper>

							<SectionWrapper title="予約手数料">
								<ProTicketCurrentTicketSection
									ticketProListAvailableSingleBilling={ticketProListAvailableSingleBilling}
									billingStatus={billingStatus}
									onPurchaseProBillingTicket={onPurchaseProBillingTicket}
									handleOpenChange={handleOpenChange}
									openBillingSettingDialog={openBillingSettingDialog}
									activateBillingSettingDialog={activateBillingSettingDialog}
									deactivateBillingSettingDialog={deactivateBillingSettingDialog}
								/>

								<ProTicketRecurringPurchaseTicketSection
									ticketProListAvailableContinuousBilling={ticketProListAvailableContinuousBilling}
									billingStatus={billingStatus}
									onRecurringPurchaseProBillingTicket={onRecurringPurchaseProBillingTicket}
									handleOpenChange={handleOpenChange}
									openBillingSettingDialog={openBillingSettingDialog}
									activateBillingSettingDialog={activateBillingSettingDialog}
									deactivateBillingSettingDialog={deactivateBillingSettingDialog}
								/>
							</SectionWrapper>

							<div className="flex justify-end pt-6">
								<Button variant="link" type="button" className="pr-0" onClick={navigateToHotelTopPage}>
									今は登録しない
								</Button>
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		);
	},
);

SettingsBillingDialog.displayName = "SettingsBillingDialog";
